import { Wrench } from "lucide-react";

import type { AnalysisMessage } from "@/api/types";

interface Props {
  message: AnalysisMessage;
}

export function ChatMessage({ message }: Props) {
  const ts = formatTimestamp(message.timestamp);

  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%] rounded-lg bg-primary px-4 py-2 text-primary-foreground">
          <div className="whitespace-pre-wrap text-sm">{message.content}</div>
          <div className="mt-1 text-xs opacity-70">{ts}</div>
        </div>
      </div>
    );
  }

  if (message.role === "assistant") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[78%] rounded-lg border bg-card px-4 py-2 shadow-sm">
          <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
            AROC
          </div>
          <div className="whitespace-pre-wrap text-sm leading-relaxed">
            {message.content || <span className="italic text-muted-foreground">(sin texto)</span>}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{ts}</div>
        </div>
      </div>
    );
  }

  if (message.role === "tool_call") {
    return (
      <div className="flex justify-start text-xs text-muted-foreground">
        <details className="group max-w-[78%] rounded-md border border-dashed bg-muted/40 px-3 py-2">
          <summary className="cursor-pointer select-none">
            <Wrench className="mr-1 inline size-3 align-text-bottom" />
            consultando <span className="font-mono">{message.tool_name}</span>
          </summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all rounded bg-muted/60 p-2 text-xs">
            {JSON.stringify(message.tool_input, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  if (message.role === "tool_result") {
    return (
      <div className="flex justify-start text-xs text-muted-foreground">
        <details className="group max-w-[78%] rounded-md border border-dashed bg-muted/30 px-3 py-2">
          <summary className="cursor-pointer select-none">
            resultado de <span className="font-mono">{message.tool_name}</span>
          </summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all rounded bg-muted/60 p-2 text-xs">
            {message.content}
          </pre>
        </details>
      </div>
    );
  }

  if (message.role === "command") {
    const args = (message.command_args ?? []).join(" ");
    return (
      <div className="flex justify-end">
        <div className="rounded bg-amber-100 px-3 py-1 font-mono text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
          /{message.command_name}
          {args ? ` ${args}` : ""}
        </div>
      </div>
    );
  }

  // system
  return (
    <div className="flex justify-center">
      <div className="rounded bg-muted px-3 py-1 text-xs italic text-muted-foreground">
        {message.content}
      </div>
    </div>
  );
}

function formatTimestamp(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString();
  } catch {
    return iso;
  }
}
